import { useQuery } from 'react-query'
import './index.scss'
import { getMobileLocations } from '../../services/calls/positions'
import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import FlotiMap from '../../components/FlotiMap'
import LiveMap_Sidebar from './sidebar'
import UserContext from '../../contexts/UserContext/context'
import { initial_sidebar } from '../../contexts/SidebarContext/reducer'
import { useSidebar } from '../../hooks/useSidebar'
import { ICON } from '../../components/Icon/data'
import { FlotiMapRef } from '../../components/FlotiMap/type'
import { useTranslation } from 'react-i18next'
import Icon from '../../components/Icon'
import { COLOR, COOKIE } from '../../data/variables'
import { useCookies } from '../../hooks/useCookies'
import { getMobileProfile } from '../../services/calls/mobiles'
import MobileProfile from './sidebar/table/MobileDetailCol/MobileProfile'
import { useWindowSize } from '../../hooks/useWindowSize'
import Button from '../../components/Button'

const LiveMap = () => {
  const { user } = useContext(UserContext)
  const sidebarRef = useRef()
  const mapRef = useRef<FlotiMapRef>()

  const { updateSidebar, updateInfobar } = useSidebar()
  const { t } = useTranslation()
  const { size, isFullSize } = useWindowSize();

  const { getCookie, setCookie } = useCookies()
  const defaultFleet = getCookie(COOKIE.LIVEMAP_FLEET_SELECTION)
    ? Number(getCookie(COOKIE.LIVEMAP_FLEET_SELECTION))
    : user?.main_fleet ?? user?.available_fleets?.[0]?.fleetid
  const [fleet, setFleet] = useState<number>(defaultFleet)

  const { data, isLoading } = useQuery({
    queryKey: 'getMobileLocations',
    queryFn: () => getMobileLocations(fleet),
    refetchInterval: 5000
  })

  useEffect(() => {
    if (fleet) {
      const fleetName = user?.available_fleets?.find((f) => f.fleetid === Number(fleet))?.name ?? t('Unknown fleet')
      mapRef?.current?.setInfoDisplay({
        label: (
          <>
            <Icon icon={ICON.CARS} color={COLOR.WHITE} />
            {t('Fleet')}
          </>
        ),
        content: fleetName
      })
    }
  }, [fleet])
  
  useEffect(() => {
    updateSidebar({
      content: (
        <LiveMap_Sidebar
          ref={sidebarRef}
          mapRef={mapRef}
          onFleetChange={(val) => {
            setFleet(val)
            setCookie(COOKIE.LIVEMAP_FLEET_SELECTION, val.toString())
          }}
          defaultFleet={fleet}
        />
      ),
      isOpen: isFullSize,
      width: isFullSize ? 660 : size.width,
      header: isFullSize ? undefined : t('Live map data'),
    })

    return () => {
      updateSidebar(initial_sidebar)
    }
  }, [])

  useEffect(() => {
    if (sidebarRef?.current) sidebarRef?.current?.setIsLoading(isLoading)
  }, [isLoading, sidebarRef.current])

  useEffect(() => {
    if (data && sidebarRef.current) {
      sidebarRef.current.setData(data.mobile_locations)
      const mapPoints = data.mobile_locations?.map((point) => {
        const { lat, lon, mobile_globalid, icon_color, mobileid, heading, icon } = point
        const marker = icon?.split('_')?.[0] ?? 'arrow'
        return {
          lat,
          lon,
          heading,
          name: mobile_globalid,
          icon: marker === 'arrow' ? ICON.MAP_MARKER : ICON.MARKER_CIRCLE,
          color: icon_color,
          id: mobileid
        }
      })
      mapRef?.current?.setPoints(mapPoints)
    }
  }, [data, sidebarRef.current])

  const onMarkerClick = useCallback(async(mobileid: number) => {
    const details = await getMobileProfile(mobileid)
    if (details) {
      updateInfobar({
        isOpen: true,
        header: (
          <div className="Sidebar__header-label">
            {`${t('Car details for')}: `}<strong>{details.automobile.registration_number}</strong>
          </div>
        ),
        content: <MobileProfile data={details} />
      })
    }
  }, [updateInfobar, t])

  const openMobileSidebar = useCallback(() => {
    updateSidebar({ isOpen: true })
  }, [updateSidebar])

  return (
    <div className="LiveMap">
      <div className="LiveMap__sidebar-button">
        <Button
          label={
            <>
              {t('Show data')}
              <Icon icon={ICON.LIST_BOX} color={COLOR.WHITE} />
            </>
          }
          size='s'
          onClick={openMobileSidebar}
        />
      </div>
      <FlotiMap
        ref={mapRef}
        onMarkerClick={onMarkerClick}
        options={{
          showClusterMarkers: true,
          showWeather: true,
          showRoadConditions: true,
          showLiveCams: true,
        }}
      />
    </div>
  )
}

export default LiveMap
